import React, { useEffect, useState, useCallback } from "react";
import { Activity, Power, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { logError } from "../lib/logger";

const fmt = (v, d = 2) =>
  v == null ? "—" : Number(v).toLocaleString("it-IT", { minimumFractionDigits: d, maximumFractionDigits: d });

function Stat({ label, value, color }) {
  return (
    <div className="bg-black/30 border border-white/10 rounded-md p-3">
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className={`font-mono text-lg font-semibold mt-0.5 ${color || "text-white"}`}>{value}</div>
    </div>
  );
}

export function MtPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/mt/live");
      setData(data);
    } catch (e) {
      logError("Errore stato MT:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const toggleMirror = async () => {
    setToggling(true);
    try {
      const { data: r } = await api.post("/admin/mt/mirror", { enabled: !data.mirror_enabled });
      setData((d) => ({ ...d, mirror_enabled: r.mirror_enabled }));
      toast.success(r.mirror_enabled ? "Auto-trading ATTIVATO" : "Auto-trading DISATTIVATO");
    } catch (e) {
      toast.error("Impossibile aggiornare l'interruttore");
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="bg-[#141414] border border-emerald-500/30 rounded-md p-6 mb-6" data-testid="mt-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Activity size={18} className="text-emerald-400" /> Auto-Trading MetaTrader
        </h2>
        <button data-testid="mt-refresh-btn" onClick={load}
          className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors">
          <RefreshCw size={13} /> Aggiorna
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 py-6 text-center">Caricamento stato conto...</div>
      ) : !data?.enabled ? (
        <div data-testid="mt-disabled" className="text-sm text-zinc-400 py-4">
          MetaApi non configurato (token mancante in backend).
        </div>
      ) : data.error ? (
        <div data-testid="mt-error" className="text-sm text-amber-400 py-4">
          Connessione in corso o errore: {String(data.error).slice(0, 140)}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 p-3 rounded-md bg-black/30 border border-white/10 mb-4">
            <div>
              <div className="text-sm font-medium text-white">Invio automatico ordini</div>
              <div className="text-xs text-zinc-500">
                {data.mirror_enabled ? "Attivo: ogni nuovo segnale apre un ordine su MT" : "Disattivo: nessun ordine inviato da questo ambiente"}
              </div>
            </div>
            <button
              data-testid="mt-mirror-toggle"
              onClick={toggleMirror}
              disabled={toggling}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 ${
                data.mirror_enabled ? "bg-emerald-600 text-white hover:bg-emerald-500" : "bg-white/5 border border-white/15 text-zinc-300 hover:bg-white/10"
              }`}
            >
              <Power size={15} /> {data.mirror_enabled ? "ON" : "OFF"}
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Stat label="Saldo" value={`${fmt(data.balance)} ${data.currency || ""}`} />
            <Stat label="Equity" value={`${fmt(data.equity)} ${data.currency || ""}`} color="text-blue-400" />
            <Stat label="Posizioni" value={data.positions?.length ?? 0} color="text-emerald-400" />
            <Stat label="Ordini pendenti" value={data.orders?.length ?? 0} color="text-amber-400" />
          </div>

          <MtTable title="Posizioni aperte" rows={data.positions} testid="mt-positions" showProfit />
          <MtTable title="Ordini pendenti" rows={data.orders} testid="mt-orders" />
        </>
      )}
    </div>
  );
}

function MtTable({ title, rows, testid, showProfit }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="mb-3" data-testid={`${testid}-empty`}>
        <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{title}</div>
        <div className="text-xs text-zinc-600 py-2">Nessuno.</div>
      </div>
    );
  }
  return (
    <div className="mb-3" data-testid={testid}>
      <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">{title} ({rows.length})</div>
      <div className="space-y-1">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center justify-between text-xs font-mono bg-black/20 rounded px-3 py-2 border border-white/5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-zinc-500">{r.comment || `#${r.id}`}</span>
              <span className="text-white font-semibold">{r.symbol}</span>
              <span className={r.type?.includes("BUY") ? "text-emerald-400" : "text-red-400"}>{r.type}</span>
              <span className="text-zinc-400">{r.volume} lot @ {fmt(r.openPrice, 2)}</span>
            </div>
            {showProfit && (
              <span className={`font-semibold ${r.profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {r.profit >= 0 ? "+" : ""}{fmt(r.profit)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
